import { useEffect, useRef, useState } from "react";
import {
  Room,
  RoomEvent,
  Track,
  VideoPreset,
  VideoPresets,
  type RemoteAudioTrack,
  type RemoteParticipant,
  type RemoteVideoTrack,
} from "livekit-client";
import { DEFAULT_AUDIO, readAudioState, type AudioState } from "./audioMode";

export interface ParticipantMedia {
  connected: boolean;
  video?: RemoteVideoTrack;
  audio?: RemoteAudioTrack;
  cameraOn?: boolean;
  micOn?: boolean;
  /** Candidate pressed "I need a proctor". */
  help?: boolean;
  /** Published by the candidate's player: "exam" once the exam runs, "waiting" before. */
  phase?: string;
}

type Status = "connecting" | "connected" | "error";

/// Subscribe-only connection to the term's proctoring room. Candidates' own track permissions
/// decide (at the SFU) which streams this staff member actually receives.
export const useProctoringRoom = (serverUrl?: string, token?: string) => {
  const [media, setMedia] = useState<Record<string, ParticipantMedia>>({});
  const [status, setStatus] = useState<Status>("connecting");
  const [error, setError] = useState<string | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [openAudio, setOpenAudio] = useState<AudioState>(DEFAULT_AUDIO);
  const [speaking, setSpeaking] = useState<Set<string>>(() => new Set());
  // Session refetches return a fresh token; only the first one is used to connect.
  const tokenRef = useRef(token);
  if (!tokenRef.current && token) tokenRef.current = token;
  const firstToken = tokenRef.current;

  useEffect(() => {
    if (!serverUrl || !firstToken) return;
    let disposed = false;
    // The proctor's optional camera reaches candidates on slow or distant links, so it is capped like theirs.
    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
      videoCaptureDefaults: { resolution: { ...VideoPresets.h360.resolution, frameRate: 15 } },
      publishDefaults: {
        simulcast: true,
        videoEncoding: { maxBitrate: 350_000, maxFramerate: 15 },
        videoSimulcastLayers: [new VideoPreset(320, 180, 120_000, 10)],
        dtx: true,
        red: true,
      },
    });

    // Always re-read the participant currently in the room: a reconnect with the same identity can
    // deliver the old session's disconnect/unsubscribe events after the new session is already live.
    const refresh = (identity: string) => {
      if (disposed) return;
      const participant = room.remoteParticipants.get(identity);
      if (!participant) {
        setMedia((prev) => ({ ...prev, [identity]: { connected: false } }));
        return;
      }
      let video: RemoteVideoTrack | undefined;
      let audio: RemoteAudioTrack | undefined;
      participant.trackPublications.forEach((pub) => {
        if (!pub.track) return;
        if (pub.kind === Track.Kind.Video) video = pub.track as RemoteVideoTrack;
        if (pub.kind === Track.Kind.Audio) audio = pub.track as RemoteAudioTrack;
      });
      const camera = participant.getTrackPublication(Track.Source.Camera);
      const mic = participant.getTrackPublication(Track.Source.Microphone);
      setMedia((prev) => ({
        ...prev,
        [identity]: {
          connected: true,
          video,
          audio,
          cameraOn: !!camera && !camera.isMuted,
          micOn: !!mic && !mic.isMuted,
          help: participant.attributes?.help === "1",
          phase: participant.attributes?.phase,
        },
      }));
    };
    const onParticipant = (participant: RemoteParticipant) => refresh(participant.identity);

    room
      .on(RoomEvent.ParticipantConnected, onParticipant)
      .on(RoomEvent.ParticipantDisconnected, onParticipant)
      .on(RoomEvent.TrackSubscribed, (_t, _p, participant) => onParticipant(participant))
      .on(RoomEvent.TrackUnsubscribed, (_t, _p, participant) => onParticipant(participant))
      .on(RoomEvent.TrackMuted, (_p, participant) => refresh(participant.identity))
      .on(RoomEvent.TrackUnmuted, (_p, participant) => refresh(participant.identity))
      .on(RoomEvent.TrackPublished, (_p, participant) => onParticipant(participant))
      .on(RoomEvent.TrackUnpublished, (_p, participant) => onParticipant(participant))
      .on(RoomEvent.ParticipantAttributesChanged, (_changed, participant) => refresh(participant.identity))
      .on(RoomEvent.RoomMetadataChanged, (metadata) => !disposed && setOpenAudio(readAudioState(metadata)))
      .on(RoomEvent.ActiveSpeakersChanged, (speakers) => !disposed && setSpeaking(new Set(speakers.map((p) => p.identity))))
      .on(RoomEvent.Disconnected, () => !disposed && setStatus("error"));

    room
      .connect(serverUrl, firstToken)
      .then(() => {
        if (disposed) return;
        // Staff tracks reach nobody until the proctor explicitly talks to someone.
        room.localParticipant.setTrackSubscriptionPermissions(false, []);
        setStatus("connected");
        setRoom(room);
        setOpenAudio(readAudioState(room.metadata));
        room.remoteParticipants.forEach(onParticipant);
      })
      .catch((e) => {
        console.error("LiveKit proctoring connect error:", e);
        if (!disposed) {
          setStatus("error");
          setError(e?.message ?? null);
        }
      });

    return () => {
      disposed = true;
      setRoom(null);
      room.disconnect();
    };
  }, [serverUrl, firstToken]);

  return { media, status, error, room, audio: openAudio, speaking };
};
